/**
 * Competitor comparison pages, served at /vs/:slug.
 *
 * These are the highest-commercial-intent pages on the site: someone searching
 * "chatbase alternative" has already decided to buy something. Two rules govern
 * every word here, and breaking either costs more than the page earns:
 *
 *   1. EVERY price claim is dated and linked. Competitor pricing moves; a stale
 *      number on a page whose entire argument is "we are honest about pricing"
 *      is self-defeating. Re-verify quarterly and bump `verifiedOn`.
 *   2. EVERY page says plainly when the competitor is the better buy. A page
 *      that claims to win on all axes reads as marketing and converts worse
 *      than one that concedes the cases it genuinely loses.
 *
 * Wisdone's own numbers come from TIER_LIMITS in feature-gate.service.ts and the
 * landing page's pricingPlans. Keep them in step.
 */

export interface PriceRow {
  /** What is being priced, e.g. "Monthly cost". */
  label: string
  wisdone: string
  competitor: string
  /** Optional clarification shown under the row. */
  note?: string
}

export interface DifferenceRow {
  dimension: string
  wisdone: string
  competitor: string
}

export interface Comparison {
  /** URL segment: /vs/<slug> */
  slug: string
  /** Display name used throughout the page copy. */
  competitor: string
  /** <title> — under ~60 chars so Google does not truncate it. */
  title: string
  /** Meta description, 150-160 chars. */
  metaDescription: string
  h1: string
  /** One sentence under the H1. */
  tagline: string
  /** Opening paragraphs. Lead with the honest framing, not the pitch. */
  intro: string[]
  /** The single sharpest contrast — the reason this page exists. */
  headline: {claim: string, detail: string}
  pricing: {
    /** The volume being priced, stated plainly so the comparison is checkable. */
    scenario: string
    /** How volumes were made comparable. Shown as a caveat — never hide this. */
    basis?: string
    rows: PriceRow[]
    verifiedOn: string
    sourceLabel: string
    sourceUrl: string
  }
  differences: DifferenceRow[]
  /** The honest section. Non-negotiable on every page. */
  chooseThem: {title: string, reasons: string[]}
  /** Closing summary — who each product is actually for. */
  verdict: string
}

export const COMPARISONS: Comparison[] = [

  // ─────────────────────────────────────────────────────────────────────────
  {
    slug: 'chatbase',
    competitor: 'Chatbase',
    title: 'Wisdone vs Chatbase — A Simpler Chatbase Alternative',
    metaDescription: 'Chatbase bills in credits that cost more on better models, and $99/month to remove its badge. Wisdone bills flat messages from $19. Honest side-by-side.',
    h1: 'Wisdone vs Chatbase',
    tagline: 'The same idea — an AI trained on your content — priced in a way you can predict.',
    intro: [
      'Chatbase and Wisdone solve the same problem: you point them at your website, they answer visitor questions from it. If you are comparing the two, you have already decided that a RAG-based assistant beats a decision-tree chatbot. That part is settled.',
      'The difference is what happens to your bill when the thing actually gets used, and what your visitors see when it answers.'
    ],
    headline: {
      claim: 'Chatbase charges credits. Better models cost more credits.',
      detail: 'A Chatbase credit is not an answer. Its basic models spend one credit per response, but Claude Sonnet spends three and Claude Opus spends five. So the $40/month Hobby plan is 700 answers on a basic model — or 233 on Sonnet, or 140 on Opus. Wisdone counts visitor messages: your plan decides which models you can use, but choosing a better one never shrinks your allowance.'
    },
    pricing: {
      scenario: 'Roughly 1,000 visitor questions a month, answered by Claude Sonnet',
      basis: 'Chatbase figures use monthly billing and 3 credits per Claude Sonnet response, per Chatbase\'s own model guide; on a basic model the same plan stretches three times further. Annual billing lowers Chatbase to $32/$120/$400. On Wisdone, Claude models (Sonnet included) start on Pro; Starter runs on OpenAI models.',
      rows: [
        {
          label: 'Entry paid plan',
          wisdone: '$19/mo — Starter',
          competitor: '$40/mo — Hobby'
        },
        {
          label: 'What that includes',
          wisdone: '1,000 messages on OpenAI models',
          competitor: '700 credits — 700 answers on a basic model, ≈ 233 on Claude Sonnet',
          note: 'Wisdone message counts are the same on every model your plan includes.'
        },
        {
          label: 'Plan needed for ~1,000 Sonnet answers',
          wisdone: '$59/mo — Pro (10,000 messages)',
          competitor: '$150/mo — Standard (4,000 credits ≈ 1,333 answers)',
          note: 'Hobby plus credit top-ups gets there too, but costs $160 — more than Standard.'
        },
        {
          label: 'Extra capacity',
          wisdone: '$20 per 500 messages',
          competitor: '$40 per 1,000 credits (≈ 333 Sonnet answers)'
        },
        {
          label: 'Removing the vendor badge',
          wisdone: 'Nothing to remove — no Wisdone badge on any plan',
          competitor: '$99/mo add-on'
        },
        {
          label: 'Free tier',
          wisdone: '50 messages/mo, no expiry',
          competitor: '50 credits/mo; agents deleted after 14 days inactive'
        }
      ],
      verifiedOn: '24 September 2026',
      sourceLabel: 'Chatbase pricing page',
      sourceUrl: 'https://www.chatbase.co/pricing'
    },
    differences: [
      {
        dimension: 'How usage is counted',
        wisdone: 'Visitor messages. One message is one message on every model your plan includes.',
        competitor: 'Credits, 1–5 per response depending on the model you choose.'
      },
      {
        dimension: 'Source citations',
        wisdone: 'On by default, linking to the exact page section the answer came from.',
        competitor: 'Available, but not the centre of the product.'
      },
      {
        dimension: 'Removing vendor branding',
        wisdone: 'None to remove. The widget carries your branding only, on every plan including Free.',
        competitor: 'A $99/mo add-on on top of the plan.'
      },
      {
        dimension: 'Lead capture',
        wisdone: 'Collected conversationally mid-chat, with custom fields and validation.',
        competitor: 'A form shown in the chat window.'
      },
      {
        dimension: 'Content-gap reporting',
        wisdone: 'Every question the bot could not answer is logged as a to-do list for your content.',
        competitor: 'Conversation history and analytics.'
      },
      {
        dimension: 'Human handoff',
        wisdone: 'Escalates to Slack, email or a webhook on every plan. Your team replies in the Slack thread and the visitor sees it live in the widget.',
        competitor: 'Its own helpdesk, or Zendesk, Intercom, Freshdesk and similar, from the $150/mo Standard plan.'
      },
      {
        dimension: 'Where the bot answers',
        wisdone: 'Your website.',
        competitor: 'Website, plus WhatsApp, Instagram, Messenger and Slack from the $150/mo Standard plan.'
      }
    ],
    chooseThem: {
      title: 'When Chatbase is the better buy',
      reasons: [
        'You need the bot itself answering inside WhatsApp, Instagram, Messenger or Slack. Wisdone answers on your website — Slack is where your team picks up handoffs, not a channel the bot replies in. If your customers reach you in DMs, Chatbase covers ground we do not.',
        'You want to pick from a long menu of models and switch between them often. Chatbase exposes far more model choice than we do.',
        'You want a vendor with a long public track record. Chatbase has been around longer and has thousands of public reviews; Wisdone is newer and has fewer. If buying from an established name matters to you, that is a legitimate reason.'
      ]
    },
    verdict: 'If your customers message you in WhatsApp, Instagram or Messenger and you want the bot answering there, Chatbase covers more ground. If they find you on your website — with your team a Slack thread away when the bot hands over — and you want a bill you can predict a year out, that is the case for Wisdone.'
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    slug: 'intercom-fin',
    competitor: 'Intercom Fin',
    title: 'Wisdone vs Intercom Fin — Flat Pricing, No Per-Answer Fee',
    metaDescription: 'Fin charges $0.99 per outcome, including when the customer simply stops asking. Wisdone is flat from $19/month, AI included. The arithmetic, in full.',
    h1: 'Wisdone vs Intercom Fin',
    tagline: 'One of these gets more expensive the better it works.',
    intro: [
      'Fin is a genuinely strong product, and Intercom is a genuinely strong helpdesk. This page is not going to pretend otherwise. If you run a support team with agents, queues and SLAs, Intercom does things Wisdone does not attempt.',
      'But a large number of people arrive at Fin wanting one specific thing — an AI that answers questions from their documentation on their website — and get quoted for a helpdesk plus a per-answer fee. If that is you, the arithmetic below is the whole argument.'
    ],
    headline: {
      claim: 'Fin bills $0.99 per outcome. Success is the thing you pay for.',
      detail: 'An outcome is not only a confirmed fix. Intercom also bills when Fin answers and the customer simply does not ask for more help, and when Fin completes a procedure — handoffs included. So the better Fin performs, the larger the invoice, and the line item that grows fastest is the one you were hoping would grow.'
    },
    pricing: {
      scenario: '2,000 customer conversations a month handled by AI',
      basis: 'Fin bills per outcome, at most once per conversation; Wisdone bills per visitor message. A conversation is assumed here to be about four visitor messages, so 2,000 conversations ≈ 8,000 messages, and every conversation is assumed to end in a billable outcome. Intercom figures use one Essential seat on annual billing.',
      rows: [
        {
          label: 'Platform / seat cost',
          wisdone: '$0 — no seats',
          competitor: '$29/seat/mo (annual), $39 monthly'
        },
        {
          label: 'AI cost at 2,000 conversations',
          wisdone: '$0 — included',
          competitor: '$1,980/mo at $0.99 each'
        },
        {
          label: 'Total for one person',
          wisdone: '$59/mo — Pro, 10,000 messages',
          competitor: '≈ $2,009/mo',
          note: 'Wisdone Pro covers roughly 2,500 conversations at the same four-messages-each assumption.'
        },
        {
          label: 'Cost if volume doubles',
          wisdone: '$199/mo — Enterprise, unlimited messages',
          competitor: '≈ $3,989/mo — the AI line doubles with it',
          note: '4,000 conversations is about 16,000 messages, past Pro\'s 10,000 — so Wisdone moves up a plan too, once, and then stops moving.'
        },
        {
          label: 'Free tier',
          wisdone: '50 messages/mo, no credit card',
          competitor: 'No free tier; 14-day trial'
        }
      ],
      verifiedOn: '24 September 2026',
      sourceLabel: 'Intercom pricing page',
      sourceUrl: 'https://www.intercom.com/pricing'
    },
    differences: [
      {
        dimension: 'Pricing model',
        wisdone: 'Flat monthly. The bill does not move when the assistant does well.',
        competitor: 'Per seat, plus $0.99 for every outcome on top.'
      },
      {
        dimension: 'What you are buying',
        wisdone: 'An AI answer layer for your website.',
        competitor: 'A full helpdesk — inbox, ticketing, multi-channel — with AI on top.'
      },
      {
        dimension: 'Setup',
        wisdone: 'Paste a URL, customise, embed one script tag.',
        competitor: 'A platform implementation; typically weeks, not minutes.'
      },
      {
        dimension: 'Human agents',
        wisdone: 'Escalation hands off to Slack, email or a webhook. No agent inbox.',
        competitor: 'A full agent inbox with routing, assignment and workload management.'
      },
      {
        dimension: 'Budget predictability',
        wisdone: 'Known a year ahead.',
        competitor: 'Varies with traffic; product launches and seasonal spikes move it sharply.'
      }
    ],
    chooseThem: {
      title: 'When Intercom is the better buy',
      reasons: [
        'You have a support team, not just a website. If people need an inbox to work out of, with assignment, queues and SLAs, Intercom is built for that and Wisdone is not.',
        'You need one thread per customer across email, in-app, social and chat. Wisdone answers on your website; Intercom unifies channels.',
        'You want AI that takes actions inside your other systems — issuing refunds, changing subscriptions. Fin does considerably more than answer questions.',
        'You already run Zendesk or Salesforce. Fin can be bought on its own and plugged into an existing helpdesk, with no Intercom seats. If your helpdesk is already staffed and paid for, adding Fin is a smaller change than adding a second product.'
      ]
    },
    verdict: 'Intercom is a helpdesk with an AI in it. Wisdone is an AI that answers from your content. If you need the helpdesk, buy the helpdesk — but if you only ever wanted the answering part, you should not be paying per answer for it.'
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    slug: 'tidio-lyro',
    competitor: 'Tidio Lyro',
    title: 'Wisdone vs Tidio Lyro — AI Answers Without the Add-Ons',
    metaDescription: 'Tidio\'s Lyro AI is a separate subscription — $39/month buys 50 AI conversations. Wisdone is one flat price from $19 with AI included. Honest side-by-side.',
    h1: 'Wisdone vs Tidio Lyro',
    tagline: 'Tidio is a live chat suite with AI sold separately. Wisdone is just the AI, priced as one thing.',
    intro: [
      'Tidio is a good live chat product with a long history in e-commerce, and Lyro is the AI bolted onto it. If your team answers chats by hand and you want AI to take the easy ones, Tidio is a sensible place to look.',
      'The thing to understand before you commit is that Lyro is a separate subscription, priced by how many AI conversations you buy each month, and it sits alongside whichever Tidio plan you are on. The advertised plan price does not include it.'
    ],
    headline: {
      claim: 'The AI is an add-on, and add-ons stack.',
      detail: 'Lyro starts at $39/month for 50 AI conversations, billed separately from your Tidio plan. Put it on Tidio Starter ($29) with the Flows automation builder ($29) and the entry price is $97/month — for 50 AI conversations. Wisdone Starter is $19 for 1,000 visitor messages, roughly 250 conversations.'
    },
    pricing: {
      scenario: 'What the entry price buys for AI answers on your website',
      basis: 'Tidio figures use monthly billing; annual billing takes about 17% off. Tidio publishes only the entry tier for Lyro and Flows — larger tiers are quoted in its pricing calculator — so only the entry tiers are priced here. Wisdone bills per visitor message; a conversation is assumed to be about four messages.',
      rows: [
        {
          label: 'Base plan',
          wisdone: '$19/mo — Starter',
          competitor: '$0–59/mo — Free, Starter ($29) or Growth ($59)',
          note: 'Lyro can run on Tidio\'s Free plan; a paid plan matters once your team handles more than 50 chats a month by hand.'
        },
        {
          label: 'AI answers',
          wisdone: 'Included — 1,000 messages (≈ 250 conversations)',
          competitor: '+ $39/mo for 50 Lyro conversations'
        },
        {
          label: 'Cost per AI conversation at entry price',
          wisdone: '≈ $0.08',
          competitor: '≈ $0.78'
        },
        {
          label: 'Automation builder',
          wisdone: 'Smart CTAs included',
          competitor: '+ $29/mo for Flows (2,000 visitors reached)'
        },
        {
          label: 'Entry total with AI and automation',
          wisdone: '$19/mo',
          competitor: '$68–127/mo',
          note: 'Tidio\'s total buys 50 AI conversations; Wisdone\'s covers roughly 250.'
        },
        {
          label: 'Free tier',
          wisdone: '50 messages/mo, no expiry',
          competitor: '50 Lyro conversations once, not monthly'
        }
      ],
      verifiedOn: '24 September 2026',
      sourceLabel: 'Tidio pricing page',
      sourceUrl: 'https://www.tidio.com/pricing/'
    },
    differences: [
      {
        dimension: 'What you buy',
        wisdone: 'One product, one price, AI included.',
        competitor: 'A plan, plus Lyro, plus Flows — priced separately.'
      },
      {
        dimension: 'How AI usage is counted',
        wisdone: 'Visitor messages against a flat monthly allowance.',
        competitor: 'Per AI conversation, bought in monthly tiers as its own subscription.'
      },
      {
        dimension: 'Live human chat',
        wisdone: 'Escalates to Slack, email or a webhook; your team can reply from the Slack thread and the visitor sees it in the widget. No built-in agent console.',
        competitor: 'A full live chat console — its original and strongest feature.'
      },
      {
        dimension: 'Source citations',
        wisdone: 'Answers link to the sections they came from.',
        competitor: 'Answers from your content; citations are not the focus.'
      },
      {
        dimension: 'Channels',
        wisdone: 'Your website.',
        competitor: 'Website, Messenger, Instagram, WhatsApp, email.'
      }
    ],
    chooseThem: {
      title: 'When Tidio is the better buy',
      reasons: [
        'You want humans and AI in the same widget. Tidio began as live chat and it shows — if agents will be picking up conversations all day, its console is better than anything we offer.',
        'You sell across Instagram, Messenger and WhatsApp as well as your site. That is Tidio territory; Wisdone stays on your website.',
        'You want a visual automation builder for e-commerce flows — cart abandonment, order updates, campaign triggers. Flows does things our Smart CTAs do not.',
        'You already run Tidio. If live chat is working for you, adding Lyro is less disruptive than replacing the stack, even at the higher price.'
      ]
    },
    verdict: 'Tidio is the right call if humans will be in the conversation and you need social channels. Wisdone is the right call if you want the questions answered without staffing a chat console — and without three subscriptions to do it.'
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    slug: 'sitegpt',
    competitor: 'SiteGPT',
    title: 'Wisdone vs SiteGPT — GPT-4.1 Without the 10× Message Cost',
    metaDescription: 'SiteGPT\'s $59 plan gives 4,000 messages on GPT-4.1-mini but just 400 on GPT-4.1. Wisdone Starter is $19 for 1,000 messages on either. Honest side-by-side.',
    h1: 'Wisdone vs SiteGPT',
    tagline: 'Two tools built for the same job. One charges ten times over for the better model.',
    intro: [
      'SiteGPT is the closest thing to Wisdone on the market. Point it at your website, it trains on your pages, and it answers visitors from them. Both run on OpenAI models, and both are built for small teams rather than support departments.',
      'So the comparison comes down to three things: how the message allowance is counted, what is included versus sold as an add-on, and how much of your site each one will read.'
    ],
    headline: {
      claim: 'On SiteGPT, the better model costs ten messages per answer.',
      detail: 'SiteGPT advertises 4,000 messages on its $59 Starter plan. That figure is for GPT-4.1-mini. Switch to GPT-4.1 and the same plan gives you 400. Wisdone Starter is $19 for 1,000 messages, GPT-4.1 is one of the models it includes, and one answer uses one message whichever model you pick.'
    },
    pricing: {
      scenario: 'Roughly 1,000 visitor questions a month, answered by GPT-4.1',
      basis: 'Both products offer GPT-4.1, so this is a like-for-like comparison. SiteGPT figures use monthly billing and SiteGPT\'s own statement that GPT-4.1 uses ten times the allowance of GPT-4.1-mini. Annual billing lowers SiteGPT to $39/$79/$259; Wisdone bills monthly only.',
      rows: [
        {
          label: 'Entry paid plan',
          wisdone: '$19/mo — Starter',
          competitor: '$59/mo — Starter ($39 billed annually)'
        },
        {
          label: 'What that includes',
          wisdone: '1,000 messages, GPT-4.1 included',
          competitor: '4,000 messages on GPT-4.1-mini, or 400 on GPT-4.1'
        },
        {
          label: 'Plan needed for ~1,000 GPT-4.1 answers',
          wisdone: '$19/mo — Starter',
          competitor: '$129/mo — Growth ($79 billed annually)'
        },
        {
          label: 'Extra capacity',
          wisdone: '$20 per 500 messages, one-off',
          competitor: '$39/mo for 5,000 more messages, recurring'
        },
        {
          label: 'Removing the vendor badge',
          wisdone: 'Nothing to remove — no Wisdone badge on any plan',
          competitor: '$39/mo add-on'
        },
        {
          label: 'Free tier',
          wisdone: '50 messages/mo, no credit card',
          competitor: 'None; 7-day trial, credit card required'
        }
      ],
      verifiedOn: '24 September 2026',
      sourceLabel: 'SiteGPT pricing page',
      sourceUrl: 'https://sitegpt.ai/pricing'
    },
    differences: [
      {
        dimension: 'How usage is counted',
        wisdone: 'Visitor messages. One message is one message on every model your plan includes.',
        competitor: 'Messages, but a GPT-4.1 answer uses ten times the allowance of a GPT-4.1-mini one.'
      },
      {
        dimension: 'Models',
        wisdone: 'OpenAI models including GPT-4.1 on Starter; Claude models added on Pro.',
        competitor: 'GPT-4.1 and GPT-4.1-mini.'
      },
      {
        dimension: 'How much of your site it reads',
        wisdone: 'Up to 500 pages per website across 3 websites on Starter; 2,000 per website across 10 on Pro. Plus uploaded files and text.',
        competitor: '1,000 pages on Starter, 10,000 on Growth.'
      },
      {
        dimension: 'Lead capture',
        wisdone: 'Collected conversationally mid-chat, with custom fields and validation.',
        competitor: 'Built in, with custom fields, industry templates and booking links.'
      },
      {
        dimension: 'Human handoff',
        wisdone: 'Slack, email or a webhook on every plan. Your team replies in the Slack thread and the visitor sees it live in the widget.',
        competitor: 'Built-in escalation to a human. When the bot runs inside Crisp, Freshdesk or Zendesk Messaging, agents take over in that tool.'
      },
      {
        dimension: 'Where the bot answers',
        wisdone: 'Your website.',
        competitor: 'Your website, plus inside Slack, Messenger, Google Chat, Crisp, Freshdesk, Zoho SalesIQ and Zendesk Messaging — from the $129/mo Growth plan, Zendesk from Scale.'
      }
    ],
    chooseThem: {
      title: 'When SiteGPT is the better buy',
      reasons: [
        'You have one large website and a starter budget. SiteGPT Starter reads up to 1,000 pages of a single site; Wisdone Starter reads up to 500 per website, spread across three.',
        'GPT-4.1-mini is good enough for you and you will pay annually. SiteGPT Starter is then $39/mo for 4,000 messages, which is less than our $59 Pro plan at that volume.',
        'You want the bot answering beyond your website — in Messenger, Slack or Google Chat, or inside the Crisp, Freshdesk or Zendesk chat your team already works in, so agents take over there. Wisdone answers on your website and hands off to Slack, email or a webhook.',
        'You want to take over conversations from your phone. SiteGPT has an iOS app from its Growth plan.'
      ]
    },
    verdict: 'SiteGPT and Wisdone do the same job with the same family of models. The difference is the meter: SiteGPT counts a GPT-4.1 answer as ten messages, and Wisdone counts it as one. If you want the better model on a small budget, that gap is most of the price. If a single very large website is all you need indexed, SiteGPT\'s entry plan reads more of it.'
  }
]

/** Lookup by URL slug. */
export function getComparison(slug: string | null | undefined): Comparison | undefined {
  if (!slug) return undefined
  return COMPARISONS.find(c => c.slug === slug)
}

/**
 * Slugs for the sitemap. Imported by the backend's seo.route.ts — this module is
 * deliberately free of Angular imports so it can be read from either side.
 */
export const COMPARISON_SLUGS: string[] = COMPARISONS.map(c => c.slug)
